import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase';
import { useModuleAdmin } from '../useModuleAdmin';
import { normaliseRole, isAdminRole, ACTOR_BUCKET_FOR } from '../roles';
import ModuleEditPermissions from './ModuleEditPermissions';
import {
  FileText, Link as LinkIcon, FolderOpen, CheckSquare, Settings as SettingsIcon,
  Plus, Trash2, X, Upload, Download, ExternalLink, Clock, CheckCircle2, XCircle, Paperclip,
} from 'lucide-react';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

const generateId = () => `f_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const emptyApplicationForm = () => ({ title: '', description: '', file: null });

const STATUS_STYLES = {
  Pending: { bg: 'var(--color-bg-app)', color: 'var(--color-text-muted)', icon: Clock },
  Approved: { bg: 'var(--color-success-bg)', color: 'var(--color-success)', icon: CheckCircle2 },
  Rejected: { bg: 'var(--color-danger-bg)', color: 'var(--color-danger)', icon: XCircle },
};

// The status is stored on the application record and is what the filter compares, so only the
// shown word is looked up; the value never changes.
const statusKey = (v) => `formsApp.statuses.${String(v || 'Pending').toLowerCase().replace(/[^a-z]/g, '')}`;

const StatusBadge = ({ status }) => {
  const { t } = useT();
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.Pending;
  const Icon = cfg.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.15rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>
      <Icon size={12} /> {t(statusKey(status), {}, status || 'Pending')}
    </span>
  );
};

const FormsAndApplication = () => {
  const { t } = useT();
  const { activeSchoolId, userProfile, isMobile } = useOutletContext();
  const ownInitial = userProfile?.initial || '';
  const ownRoleStr = normaliseRole(userProfile?.role);
  const isAdmin = isAdminRole(ownRoleStr);
  const ownActorBucket = ACTOR_BUCKET_FOR[ownRoleStr] || '';
  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownName = userProfile?.name || auth.currentUser?.email || 'Unknown';

  const [activeTab, setActiveTab] = useState('my_application');
  const [staffList, setStaffList] = useState([]);

  // --- Settings: Categories, D-Forms, Documents, Approvers ---
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [dforms, setDforms] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [approvers, setApprovers] = useState([]);   // reference display only
  const [approveRoles, setApproveRoles] = useState([]);
  const [newApproverInitial, setNewApproverInitial] = useState('');
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const docFileInputRef = useRef(null);
  const pendingUploadRef = useRef(null);

  // Named approvers live in metadata/module_admins now, keyed by email. The old
  // forms_app_settings.approvers held INITIALS, which no security rule can verify -- so the queue
  // looked gated while the rules let any member of staff approve.
  const isModuleAdmin = useModuleAdmin(activeSchoolId, 'forms_and_application');
  const isApprover = isAdmin || isModuleAdmin || approveRoles.includes(ownActorBucket);

  // --- Applications ---
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [showAppModal, setShowAppModal] = useState(false);
  const [appForm, setAppForm] = useState(emptyApplicationForm());
  const [savingApp, setSavingApp] = useState(false);
  const [approveStatusFilter, setApproveStatusFilter] = useState('Pending');
  const [decisionTarget, setDecisionTarget] = useState(null);
  const [decisionRemarks, setDecisionRemarks] = useState('');
  const [savingDecision, setSavingDecision] = useState(false);

  useEffect(() => {
    if (!activeSchoolId) return;
    getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'))
      .then(snap => {
        const list = [];
        snap.forEach(d => {
          const data = d.data();
          if (!data.initial) return;
          list.push({ initial: data.initial, name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial });
        });
        list.sort((a, b) => a.initial.localeCompare(b.initial));
        setStaffList(list);
      })
      .catch(e => console.error('Error loading staff list', e));
  }, [activeSchoolId]);

  const staffMap = useMemo(() => Object.fromEntries(staffList.map(s => [s.initial, s])), [staffList]);

  const fetchSettings = async () => {
    if (!activeSchoolId) return;
    setSettingsLoaded(false);
    try {
      const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'forms_app_settings'));
      const data = snap.exists() ? snap.data() : {};
      setCategories(Array.isArray(data.categories) ? data.categories : []);
      setDforms(Array.isArray(data.dforms) ? data.dforms : []);
      setDocuments(Array.isArray(data.documents) ? data.documents : []);
      setApprovers(Array.isArray(data.approvers) ? data.approvers : []);
      setApproveRoles(Array.isArray(data.approvePermissions?.roles) ? data.approvePermissions.roles : []);
    } catch (e) {
      console.error('Error loading Forms & Application settings', e);
    } finally {
      setSettingsLoaded(true);
    }
  };
  useEffect(() => { fetchSettings(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeSchoolId]);

  useEffect(() => {
    if (!activeSchoolId) { setApplications([]); return; }
    setLoadingApplications(true);
    const unsub = onSnapshot(
      collection(db, 'schools', activeSchoolId, 'application_record'),
      (snap) => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''));
        setApplications(list);
        setLoadingApplications(false);
      },
      (e) => { console.error('Error loading applications', e); setLoadingApplications(false); }
    );
    return () => unsub();
  }, [activeSchoolId]);

  const myApplications = useMemo(
    () => applications.filter(a => (a.submittedByEmail || '').toLowerCase() === ownEmail),
    [applications, ownEmail]
  );
  const filteredApproveList = useMemo(
    () => applications.filter(a => approveStatusFilter === 'All' || a.status === approveStatusFilter),
    [applications, approveStatusFilter]
  );

  // --- Settings: Categories (shared between D-Forms and Documents) ---
  const addCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;
    if (categories.some(c => c.name.toLowerCase() === name.toLowerCase())) { setNewCategoryName(''); return; }
    setCategories(prev => [...prev, { id: generateId(), name }]);
    setNewCategoryName('');
  };
  const removeCategory = (id) => setCategories(prev => prev.filter(c => c.id !== id));
  const categoryMap = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c.name])), [categories]);

  const UNCATEGORIZED_KEY = '__uncategorized__';
  const groupByCategory = (items) => {
    const groups = {};
    items.forEach(item => {
      const key = item.category && categoryMap[item.category] ? item.category : UNCATEGORIZED_KEY;
      (groups[key] = groups[key] || []).push(item);
    });
    const ordered = categories.filter(c => groups[c.id]).map(c => ({ id: c.id, name: c.name, items: groups[c.id] }));
    if (groups[UNCATEGORIZED_KEY]) ordered.push({ id: UNCATEGORIZED_KEY, name: t('formsApp.uncategorized'), items: groups[UNCATEGORIZED_KEY] });
    return ordered;
  };
  const dformGroups = useMemo(() => groupByCategory(dforms), [dforms, categories, categoryMap]);
  const documentGroups = useMemo(() => groupByCategory(documents), [documents, categories, categoryMap]);

  // --- Settings: D-Forms rows ---
  const addDformRow = () => setDforms(prev => [...prev, { id: generateId(), title: '', titleZh: '', link: '', description: '', category: '' }]);
  const updateDformRow = (idx, key, val) => setDforms(prev => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  const removeDformRow = (idx) => setDforms(prev => prev.filter((_, i) => i !== idx));

  // --- Settings: Documents rows (each row may carry at most one docx file and one pdf file) ---
  const addDocumentRow = () => setDocuments(prev => [...prev, { id: generateId(), title: '', titleZh: '', description: '', category: '', docx: null, pdf: null }]);
  const updateDocumentRow = (idx, key, val) => setDocuments(prev => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  const removeDocumentRow = async (idx) => {
    const row = documents[idx];
    if (row?.docx?.storagePath) { try { await deleteObject(ref(storage, row.docx.storagePath)); } catch (e) { /* already gone */ } }
    if (row?.pdf?.storagePath) { try { await deleteObject(ref(storage, row.pdf.storagePath)); } catch (e) { /* already gone */ } }
    setDocuments(prev => prev.filter((_, i) => i !== idx));
  };

  const triggerDocUpload = (idx, kind) => {
    pendingUploadRef.current = { idx, kind };
    docFileInputRef.current?.click();
  };

  const handleDocFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const target = pendingUploadRef.current;
    pendingUploadRef.current = null;
    if (!file || !target) return;
    const { idx, kind } = target;
    const row = documents[idx];
    if (!row) return;
    const lowerName = file.name.toLowerCase();
    if (kind === 'docx' && !lowerName.endsWith('.docx')) return alert(t('formsApp.wrongDocx'));
    if (kind === 'pdf' && !lowerName.endsWith('.pdf')) return alert(t('formsApp.wrongPdf'));
    try {
      if (row[kind]?.storagePath) { try { await deleteObject(ref(storage, row[kind].storagePath)); } catch (e) { /* already gone */ } }
      const storagePath = `upload/schools/${activeSchoolId}/forms_app_documents/${row.id}/${kind}_${file.name}`;
      const fileRef = ref(storage, storagePath);
      const snapshot = await uploadBytesResumable(fileRef, file);
      const fileUrl = await getDownloadURL(snapshot.ref);
      updateDocumentRow(idx, kind, { fileName: file.name, fileUrl, storagePath });
    } catch (err) {
      console.error('Error uploading document file', err);
      alert(t('formsApp.uploadFailed', { message: err.message }));
    }
  };

  const removeDocFile = async (idx, kind) => {
    const row = documents[idx];
    if (row?.[kind]?.storagePath) { try { await deleteObject(ref(storage, row[kind].storagePath)); } catch (e) { /* already gone */ } }
    updateDocumentRow(idx, kind, null);
  };

  // --- Settings: Approvers ---
  const addApprover = () => {
    if (newApproverInitial && !approvers.includes(newApproverInitial)) setApprovers(prev => [...prev, newApproverInitial]);
    setNewApproverInitial('');
  };
  const removeApprover = (initial) => setApprovers(prev => prev.filter(a => a !== initial));
  const availableApproverOptions = staffList.filter(s => !approvers.includes(s.initial));

  const handleSaveSettings = async () => {
    const cleanDforms = dforms.map(f => ({ ...f, title: (f.title || '').trim(), titleZh: (f.titleZh || '').trim(), link: (f.link || '').trim(), description: (f.description || '').trim() })).filter(f => f.title && f.link);
    const cleanDocuments = documents.map(d => ({ ...d, title: (d.title || '').trim(), titleZh: (d.titleZh || '').trim(), description: (d.description || '').trim() })).filter(d => d.title);
    const cleanCategories = categories.map(c => ({ ...c, name: (c.name || '').trim() })).filter(c => c.name);
    setSavingSettings(true);
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'forms_app_settings'), {
        categories: cleanCategories,
        dforms: cleanDforms,
        documents: cleanDocuments,
        approvers,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      }, { merge: true });
      await fetchSettings();
      alert(t('formsApp.settingsSaved'));
    } catch (e) {
      console.error('Error saving Forms & Application settings', e);
      alert(t('formsApp.saveSettingsFailed', { message: e.message }));
    }
    setSavingSettings(false);
  };

  // --- My Application: submit / delete ---
  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    if (!appForm.title.trim()) return alert(t('formsApp.enterTitle'));
    setSavingApp(true);
    try {
      const docRef = doc(collection(db, 'schools', activeSchoolId, 'application_record'));
      let attachment = null;
      if (appForm.file) {
        const storagePath = `upload/schools/${activeSchoolId}/application_record/${docRef.id}/${appForm.file.name}`;
        const fileRef = ref(storage, storagePath);
        const snapshot = await uploadBytesResumable(fileRef, appForm.file);
        const fileUrl = await getDownloadURL(snapshot.ref);
        attachment = { fileName: appForm.file.name, fileUrl, storagePath };
      }
      await setDoc(docRef, {
        title: appForm.title.trim(),
        description: appForm.description.trim(),
        attachment,
        status: 'Pending',
        submittedByEmail: auth.currentUser?.email || '',
        submittedByInitial: ownInitial,
        submittedByName: ownName,
        submittedAt: new Date().toISOString(),
      });
      setShowAppModal(false);
      setAppForm(emptyApplicationForm());
    } catch (e) {
      console.error('Error submitting application', e);
      alert(t('formsApp.submitFailed', { message: e.message }));
    }
    setSavingApp(false);
  };

  const handleDeleteApplication = async (record) => {
    if (!window.confirm(t('formsApp.deleteConfirm'))) return;
    try {
      if (record.attachment?.storagePath) { try { await deleteObject(ref(storage, record.attachment.storagePath)); } catch (e) { /* already gone */ } }
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'application_record', record.id));
    } catch (e) {
      console.error('Error deleting application', e);
      alert(t('formsApp.deleteFailed', { message: e.message }));
    }
  };

  // --- Approve / Reject ---
  const openDecisionModal = (record, action) => { setDecisionTarget({ record, action }); setDecisionRemarks(''); };
  const handleConfirmDecision = async () => {
    if (!decisionTarget) return;
    setSavingDecision(true);
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'application_record', decisionTarget.record.id), {
        status: decisionTarget.action,
        approverRemarks: decisionRemarks.trim(),
        approvedByInitial: ownInitial,
        approvedByName: ownName,
        approvedAt: new Date().toISOString(),
      }, { merge: true });
      setDecisionTarget(null);
    } catch (e) {
      console.error('Error recording decision', e);
      alert(t('formsApp.decisionFailed', { message: e.message }));
    }
    setSavingDecision(false);
  };

  const tabs = [
    { id: 'my_application', label: t('formsApp.tabs.myApplication'), icon: FileText },
    { id: 'dforms', label: t('formsApp.tabs.dforms'), icon: LinkIcon },
    { id: 'documents', label: t('formsApp.tabs.documents'), icon: FolderOpen },
    { id: 'approve', label: t('formsApp.tabs.approve'), icon: CheckSquare },
    { id: 'settings', label: t('formsApp.tabs.settings'), icon: SettingsIcon },
  ];

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs Bar */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
                style={{ whiteSpace: 'nowrap' }}
              >
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {activeTab === 'my_application' && (
            <button className="btn-primary" onClick={() => { setAppForm(emptyApplicationForm()); setShowAppModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0 0.9rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              <Plus size={14} /> {t('formsApp.newApplication')}
            </button>
          )}
          {activeTab === 'approve' && (
            <select className="input-field" value={approveStatusFilter} onChange={e => setApproveStatusFilter(e.target.value)} style={{ width: '140px' }}>
              <option value="Pending">{t('formsApp.statuses.pending')}</option>
              <option value="Approved">{t('formsApp.statuses.approved')}</option>
              <option value="Rejected">{t('formsApp.statuses.rejected')}</option>
              <option value="All">{t('formsApp.allStatuses')}</option>
            </select>
          )}
          {activeTab === 'settings' && (
            <button className="btn-primary" onClick={handleSaveSettings} disabled={savingSettings || !settingsLoaded} style={{ padding: '0.5rem 1.25rem' }}>
              {savingSettings ? t('common.saving') : t('formsApp.saveSettings')}
            </button>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem' }}>

        {activeTab === 'my_application' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('formsApp.tabs.myApplication')}</h2>
            <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.date')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.title')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.description')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.attachment')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('common.status')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.approverRemarks')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '70px' }}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingApplications ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                  ) : myApplications.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('formsApp.noApplications')}</td></tr>
                  ) : myApplications.map((a, i) => (
                    <tr key={a.id} style={{ borderBottom: i === myApplications.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{(a.submittedAt || '').slice(0, 10) || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{a.title}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{a.description || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {a.attachment ? <a href={a.attachment.fileUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Paperclip size={13} />{a.attachment.fileName}</a> : '-'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}><StatusBadge status={a.status} /></td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{a.approverRemarks || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {a.status === 'Pending' && (
                          <button onClick={() => handleDeleteApplication(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }} title={t('common.delete')}>
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'dforms' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('formsApp.tabs.dforms')}</h2>
            {dforms.length === 0 ? (
              <div style={{ textAlign: 'center', padding: isMobile ? '1.5rem' : '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)' }}>{t('formsApp.noDforms')}</div>
            ) : (
              dformGroups.map(group => (
                <div key={group.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', margin: 0, color: 'var(--color-text-muted)' }}>{group.name}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: '1rem' }}>
                    {group.items.map(f => (
                      <div key={f.id} style={{ backgroundColor: 'var(--color-bg-card)', padding: '1.25rem', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ fontWeight: '700', fontSize: '1rem' }}>{f.title}</div>
                        {f.titleZh && <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{f.titleZh}</div>}
                        {f.description && <div style={{ fontSize: '0.85rem', color: 'var(--color-text-main)' }}>{f.description}</div>}
                        <a href={f.link} target="_blank" rel="noreferrer" className="btn-outline" style={{ marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', textDecoration: 'none', padding: '0.5rem 0.9rem' }}>
                          <ExternalLink size={15} /> {t('formsApp.openForm')}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('formsApp.tabs.documents')}</h2>
            {documents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: isMobile ? '1.5rem' : '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)' }}>{t('formsApp.noDocuments')}</div>
            ) : (
              documentGroups.map(group => (
                <div key={group.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', margin: 0, color: 'var(--color-text-muted)' }}>{group.name}</h3>
                  <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.title')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.description')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '140px' }}>Word</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '140px' }}>PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((d, i) => (
                          <tr key={d.id} style={{ borderBottom: i === group.items.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{d.title}{d.titleZh ? ` (${d.titleZh})` : ''}</td>
                            <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{d.description || '-'}</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              {d.docx ? <a href={d.docx.fileUrl} target="_blank" rel="noreferrer" className="btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.6rem', fontSize: '0.8rem', textDecoration: 'none', width: 'auto' }}><Download size={13} /> Word</a> : <span style={{ color: 'var(--color-text-muted)' }}>-</span>}
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              {d.pdf ? <a href={d.pdf.fileUrl} target="_blank" rel="noreferrer" className="btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.6rem', fontSize: '0.8rem', textDecoration: 'none', width: 'auto' }}><Download size={13} /> PDF</a> : <span style={{ color: 'var(--color-text-muted)' }}>-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'approve' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('formsApp.tabs.approve')}</h2>
            {!isApprover && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
                You are not on the approvers list, so this is a read-only view of the queue.
              </div>
            )}
            <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.date')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.submittedBy')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.title')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.description')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('formsApp.attachment')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('common.status')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '160px' }}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingApplications ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                  ) : filteredApproveList.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('formsApp.noMatchingApplications')}</td></tr>
                  ) : filteredApproveList.map((a, i) => (
                    <tr key={a.id} style={{ borderBottom: i === filteredApproveList.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{(a.submittedAt || '').slice(0, 10) || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{a.submittedByName || a.submittedByInitial || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{a.title}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{a.description || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {a.attachment ? <a href={a.attachment.fileUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Paperclip size={13} />{a.attachment.fileName}</a> : '-'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}><StatusBadge status={a.status} /></td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {isApprover && a.status === 'Pending' && (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => openDecisionModal(a, 'Approved')} className="btn-outline" style={{ width: 'auto', padding: '0.3rem 0.6rem', fontSize: '0.8rem', color: 'var(--color-success)', borderColor: 'var(--color-success)' }}>{t('formsApp.approve')}</button>
                            <button onClick={() => openDecisionModal(a, 'Rejected')} className="btn-outline" style={{ width: 'auto', padding: '0.3rem 0.6rem', fontSize: '0.8rem', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>{t('formsApp.reject')}</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>

            <ModuleEditPermissions
              settingsDoc="forms_app_settings"
              moduleId="forms_and_application"
              recordLabel="form templates and settings"
              approveLabel="approve applications"
            />
            {/* Categories */}
            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('formsApp.categoriesCount', { count: categories.length })}</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0 0 1rem' }}>
                {t('formsApp.categoriesIntro')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                {categories.length === 0 ? (
                  <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{t('formsApp.noCategories')}</span>
                ) : categories.map(c => (
                  <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.7rem', borderRadius: '999px', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', fontSize: '0.85rem' }}>
                    {c.name}
                    <button type="button" onClick={() => removeCategory(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex', padding: 0 }}>
                      <X size={13} />
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  className="input-field"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                  placeholder={t('formsApp.categoryPlaceholder')}
                  style={{ width: '260px' }}
                />
                <button type="button" className="btn-outline" onClick={addCategory} disabled={!newCategoryName.trim()} style={{ width: 'auto', padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Plus size={16} /> {t('common.add')}
                </button>
              </div>
            </div>

            {/* D-Forms */}
            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0, whiteSpace: 'nowrap' }}>{t('formsApp.dformsCount', { count: dforms.length })}</h3>
                <button type="button" className="btn-outline" onClick={addDformRow} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', width: 'auto', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> {t('common.add')}
                </button>
              </div>
              <div className="table-container" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ backgroundColor: 'var(--color-bg-app)' }}>
                    <tr>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.title')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.titleChinese')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.link')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.description')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.category')}</th>
                      <th style={{ width: '48px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dforms.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--color-text-muted)' }}>{t('formsApp.noDformsYet')}</td></tr>
                    ) : dforms.map((f, idx) => (
                      <tr key={f.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.4rem 0.5rem' }}><input className="input-field" value={f.title || ''} placeholder={t('formsApp.dformTitlePlaceholder')} onChange={e => updateDformRow(idx, 'title', e.target.value)} style={{ minWidth: '160px' }} /></td>
                        <td style={{ padding: '0.4rem 0.5rem' }}><input className="input-field" value={f.titleZh || ''} onChange={e => updateDformRow(idx, 'titleZh', e.target.value)} style={{ minWidth: '140px' }} /></td>
                        <td style={{ padding: '0.4rem 0.5rem' }}><input className="input-field" value={f.link || ''} placeholder="https://..." onChange={e => updateDformRow(idx, 'link', e.target.value)} style={{ minWidth: '220px' }} /></td>
                        <td style={{ padding: '0.4rem 0.5rem' }}><input className="input-field" value={f.description || ''} onChange={e => updateDformRow(idx, 'description', e.target.value)} style={{ minWidth: '200px' }} /></td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <select className="input-field" value={f.category || ''} onChange={e => updateDformRow(idx, 'category', e.target.value)} style={{ minWidth: '150px' }}>
                            <option value="">{t('formsApp.noCategoryOption')}</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button type="button" onClick={() => removeDformRow(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Documents */}
            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0, whiteSpace: 'nowrap' }}>{t('formsApp.documentsCount', { count: documents.length })}</h3>
                <button type="button" className="btn-outline" onClick={addDocumentRow} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', width: 'auto', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> {t('common.add')}
                </button>
              </div>
              <input type="file" ref={docFileInputRef} style={{ display: 'none' }} onChange={handleDocFileSelected} />
              <div className="table-container" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ backgroundColor: 'var(--color-bg-app)' }}>
                    <tr>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.title')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.titleChinese')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.description')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('formsApp.category')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)', width: '160px' }}>Word (.docx)</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)', width: '160px' }}>PDF</th>
                      <th style={{ width: '48px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--color-text-muted)' }}>{t('formsApp.noDocumentsYet')}</td></tr>
                    ) : documents.map((d, idx) => (
                      <tr key={d.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.4rem 0.5rem' }}><input className="input-field" value={d.title || ''} placeholder={t('formsApp.documentTitlePlaceholder')} onChange={e => updateDocumentRow(idx, 'title', e.target.value)} style={{ minWidth: '160px' }} /></td>
                        <td style={{ padding: '0.4rem 0.5rem' }}><input className="input-field" value={d.titleZh || ''} onChange={e => updateDocumentRow(idx, 'titleZh', e.target.value)} style={{ minWidth: '140px' }} /></td>
                        <td style={{ padding: '0.4rem 0.5rem' }}><input className="input-field" value={d.description || ''} onChange={e => updateDocumentRow(idx, 'description', e.target.value)} style={{ minWidth: '180px' }} /></td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <select className="input-field" value={d.category || ''} onChange={e => updateDocumentRow(idx, 'category', e.target.value)} style={{ minWidth: '150px' }}>
                            <option value="">{t('formsApp.noCategoryOption')}</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          {d.docx ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90px' }} title={d.docx.fileName}>{d.docx.fileName}</span>
                              <button type="button" onClick={() => removeDocFile(idx, 'docx')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}><X size={14} /></button>
                            </div>
                          ) : (
                            <button type="button" className="btn-outline" onClick={() => triggerDocUpload(idx, 'docx')} style={{ width: 'auto', padding: '0.3rem 0.6rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                              <Upload size={13} /> {t('formsApp.upload')}
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          {d.pdf ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90px' }} title={d.pdf.fileName}>{d.pdf.fileName}</span>
                              <button type="button" onClick={() => removeDocFile(idx, 'pdf')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}><X size={14} /></button>
                            </div>
                          ) : (
                            <button type="button" className="btn-outline" onClick={() => triggerDocUpload(idx, 'pdf')} style={{ width: 'auto', padding: '0.3rem 0.6rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                              <Upload size={13} /> {t('formsApp.upload')}
                            </button>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button type="button" onClick={() => removeDocumentRow(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Application Modal */}
      {showAppModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('formsApp.newApplication')}</h3>
              <button type="button" onClick={() => setShowAppModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmitApplication} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('formsApp.titleLabel')}</label>
                <input className="input-field" value={appForm.title} onChange={e => setAppForm(prev => ({ ...prev, title: e.target.value }))} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('formsApp.description')}</label>
                <textarea className="input-field" rows={4} value={appForm.description} onChange={e => setAppForm(prev => ({ ...prev, description: e.target.value }))} style={{ resize: 'vertical', height: 'auto', minHeight: '90px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('formsApp.attachmentOptional')}</label>
                <input type="file" onChange={e => setAppForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))} />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowAppModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={savingApp}>{savingApp ? t('formsApp.submitting') : t('common.submit')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approve / Reject Decision Modal */}
      {decisionTarget && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '440px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{decisionTarget.action === 'Approved' ? t('formsApp.approveApplication') : t('formsApp.rejectApplication')}</h3>
              <button type="button" onClick={() => setDecisionTarget(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}><strong>{decisionTarget.record.title}</strong>{' '}{t('formsApp.byLine', { name: decisionTarget.record.submittedByName || decisionTarget.record.submittedByInitial })}</p>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('formsApp.remarksOptional')}</label>
              <textarea className="input-field" rows={3} value={decisionRemarks} onChange={e => setDecisionRemarks(e.target.value)} style={{ resize: 'vertical', height: 'auto', minHeight: '70px' }} />
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setDecisionTarget(null)}>{t('common.cancel')}</button>
              <button type="button" className="btn-primary" style={{ flex: 1 }} onClick={handleConfirmDecision} disabled={savingDecision}>{savingDecision ? t('common.saving') : t(decisionTarget.action === 'Approved' ? 'formsApp.confirmApproved' : 'formsApp.confirmRejected')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsAndApplication;
